/**
 * 地图视图组件
 * 使用高德地图展示门店点位（GCJ-02坐标系）
 */

import React, { useEffect, useRef, useState } from 'react';
import AMapLoader from '@amap/amap-jsapi-loader';
import { Spin, Button, Slider } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { getPlatformStatus } from '../utils/platformFilter';

// 高德地图API密钥
const AMAP_KEY = 'b4c26c81875b344a43c16f444c9b6584';

const createMarkerIcon = (AMap, markerColor, selected) => {
  const selectedStroke = '#123b73';
  const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"><defs><filter id="s" x="-80%" y="-60%" width="260%" height="260%"><feDropShadow dx="0" dy="3" stdDeviation="${selected ? 4 : 3}" flood-color="${selected ? selectedStroke : markerColor}" flood-opacity="${selected ? .72 : .38}"/></filter></defs><path filter="url(#s)" fill="${markerColor}" stroke="${selected ? selectedStroke : '#fff'}" stroke-width="${selected ? 4 : 2.4}" d="M18,1C9.8,1,3.5,6.1,3.5,13.3C3.5,22.1,18,45.5,18,45.5S32.5,22.1,32.5,13.3C32.5,6.1,26.2,1,18,1z"/><circle cx="18" cy="13.2" r="5.3" fill="#fff"/></svg>`;
  const svgBase64 = btoa(unescape(encodeURIComponent(svgIcon)));
  return new AMap.Icon({
    size: new AMap.Size(36, 48),
    image: `data:image/svg+xml;base64,${svgBase64}`,
    imageSize: new AMap.Size(36, 48),
  });
};

const createOverviewIcon = (AMap, markerColor, selected) => {
  const stroke = selected ? '#123b73' : '#ffffff';
  const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="27" viewBox="0 0 36 48"><path fill="${markerColor}" stroke="${stroke}" stroke-width="${selected ? 4 : 2.4}" d="M18,1C9.8,1,3.5,6.1,3.5,13.3C3.5,22.1,18,45.5,18,45.5S32.5,22.1,32.5,13.3C32.5,6.1,26.2,1,18,1z"/><circle cx="18" cy="13.2" r="5.3" fill="#fff"/></svg>`;
  const svgBase64 = btoa(unescape(encodeURIComponent(svgIcon)));
  return new AMap.Icon({
    size: new AMap.Size(20, 27),
    image: `data:image/svg+xml;base64,${svgBase64}`,
    imageSize: new AMap.Size(20, 27),
  });
};

const MarkerLegendIcon = ({ color, selected = false }) => (
  <svg width="18" height="24" viewBox="0 0 36 48" aria-hidden="true" style={{ display: 'block', flex: '0 0 auto' }}>
    <path
      fill={color}
      stroke={selected ? '#123b73' : '#fff'}
      strokeWidth={selected ? 4 : 2.4}
      d="M18,1C9.8,1,3.5,6.1,3.5,13.3C3.5,22.1,18,45.5,18,45.5S32.5,22.1,32.5,13.3C32.5,6.1,26.2,1,18,1z"
    />
    <circle cx="18" cy="13.2" r="5.3" fill="#fff" />
  </svg>
);

const MapView = ({
  stores,
  selectedStoreKeys,
  onStoreToggle,
  boundaries,
  totalCount,
  meituanConfidence,
  elemeConfidence,
  onMapViewChange,
  shouldAutoFit,
  onAutoFitComplete,
  demandLayerEnabled,
  demandData,
  demandLayerMode,
  selectedCategory,
  populationLayerEnabled,
  populationData,
  selectedPopulationTypes,
  incomeLayerEnabled,
  incomeData,
  selectedIncomeLevel,
  onDrillDown,
  onGoBack,
  currentRegion,
  loading,  // 新增：加载状态
  nationalOverview = false,
}) => {
  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const polygonsRef = useRef([]);
  const boundaryLabelsRef = useRef([]);
  const demandPolygonsRef = useRef([]); // 需求图层的多边形
  const populationPolygonsRef = useRef([]); // 人口图层的多边形
  const incomePolygonsRef = useRef([]); // 收入图层的多边形
  const prevStoresRef = useRef(null);
  const prevBoundariesRef = useRef(null);
  const ignoreViewportUntilRef = useRef(0);
  const userViewportChangeRef = useRef(false);
  const lastUserViewportChangeAtRef = useRef(0);
  const lastViewportKeyRef = useRef('');
  const [hoveredStore, setHoveredStore] = useState(null);
  const [hoveredBoundary, setHoveredBoundary] = useState(null);  // 新增：悬停的边界
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [currentZoom, setCurrentZoom] = useState(5);  // 当前缩放级别

  // 初始化地图
  useEffect(() => {
    // 确保 DOM 元素已经存在
    if (!mapContainer.current) {
      console.error('Map container not ready');
      return;
    }

    AMapLoader.load({
      key: AMAP_KEY,
      version: '2.0',
      plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.InfoWindow', 'AMap.DistrictLayer'],
    })
      .then((AMap) => {
        // 再次检查容器是否存在（异步操作完成后）
        if (!mapContainer.current) {
          console.error('Map container removed before initialization');
          return;
        }

        const map = new AMap.Map(mapContainer.current, {
          zoom: 5,
          center: [108.953056, 34.268889], // 中国中心坐标
          viewMode: '2D',
          mapStyle: 'amap://styles/whitesmoke',
          features: ['bg', 'road'],
          showLabel: true,
          isHotspot: true,
          doubleClickZoom: false, // 禁用双击缩放，以支持双击下钻功能
        });

        // 添加控件（只保留比例尺，移除工具条以使用自定义缩放控件）
        map.addControl(new AMap.Scale());

        // 底图按缩放等级逐步增加细节，避免低缩放级别的信息干扰业务点位。
        const updateBaseMapDetail = zoom => {
          if (zoom >= 15) {
            map.setFeatures(['bg', 'road', 'building', 'point']);
          } else if (zoom >= 11) {
            map.setFeatures(['bg', 'road', 'point']);
          } else {
            // 保留高德原生省会和代表城市标注。
            map.setFeatures(['bg', 'road', 'point']);
          }
        };
        updateBaseMapDetail(map.getZoom());

        // 添加中国行政区域图层（显示底图轮廓）
        const districtLayer = new AMap.DistrictLayer.Country({
          zIndex: 10,
          SOC: 'CHN',
          depth: 2,
          styles: {
            'nation-stroke': '#78aee8',
            'coastline-stroke': '#78aee8',
            'province-stroke': '#a8b8ca',
            'city-stroke': '#c1ccd8',
            'county-stroke': '#d7dee7',
            'fill': 'rgba(255, 255, 255, 0)'
          }
        });
        districtLayer.setMap(map);

        // Only user-initiated navigation should load another random viewport sample.
        // Programmatic setFitView/setCenter calls also emit moveend in AMap.
        const markUserViewportChange = () => {
          userViewportChangeRef.current = true;
          lastUserViewportChangeAtRef.current = Date.now();
        };
        map.on('dragstart', markUserViewportChange);
        map.on('dragend', markUserViewportChange);
        map.on('mousewheel', markUserViewportChange);

        // 监听地图视野变化（包括拖拽、缩放）
        map.on('moveend', () => {
          const zoom = map.getZoom();
          setCurrentZoom(zoom);
          updateBaseMapDetail(zoom);

          // setFitView is programmatic and may finish well after React has
          // rendered. It must never trigger a bounds query that replaces the
          // region sample with a different local sample.
          if (Date.now() < ignoreViewportUntilRef.current) {
            userViewportChangeRef.current = false;
            return;
          }
          const interactionAge = Date.now() - lastUserViewportChangeAtRef.current;
          if (!userViewportChangeRef.current || interactionAge > 5000) {
            userViewportChangeRef.current = false;
            return;
          }
          userViewportChangeRef.current = false;
          lastUserViewportChangeAtRef.current = 0;

          // 获取当前地图的可视范围
          const bounds = map.getBounds();
          const southWest = bounds.getSouthWest();  // 西南角
          const northEast = bounds.getNorthEast();  // 东北角

          // 根据缩放级别计算合适的点位数量
          const limit = 100;

          const viewportInfo = {
            zoom: zoom,
            bounds: {
              minLng: southWest.lng,
              minLat: southWest.lat,
              maxLng: northEast.lng,
              maxLat: northEast.lat,
            },
            limit: limit
          };

          const viewportKey = [
            zoom.toFixed(2),
            southWest.lng.toFixed(5),
            southWest.lat.toFixed(5),
            northEast.lng.toFixed(5),
            northEast.lat.toFixed(5),
          ].join(':');
          if (viewportKey === lastViewportKeyRef.current) return;
          lastViewportKeyRef.current = viewportKey;

          // 通知父组件视野变化
          if (onMapViewChange) {
            onMapViewChange(viewportInfo);
          }
        });

        mapInstance.current = map;
      })
      .catch((error) => {
        console.error('地图加载失败:', error);
      });

    return () => {
      if (mapInstance.current) {
        mapInstance.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 更新地图标记
  useEffect(() => {
    if (!mapInstance.current || !window.AMap) return;

    const AMap = window.AMap;

    // 清除旧标记
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    if (!stores || stores.length === 0) {
      prevStoresRef.current = stores;
      return;
    }

    // 全国总览按缩放级别逐步增加点位；后端排序保证前几轮已覆盖各省。
    const overviewLimit = currentZoom < 5 ? 120 : currentZoom < 6.5 ? 300 : stores.length;
    const displayStores = nationalOverview ? stores.slice(0, overviewLimit) : stores;

    // 添加新标记
    const markers = displayStores.map((store) => {
      if (!store.lng || !store.lat) return null;

      // 美团和饿了么门店识别（使用传入的阈值参数）
      const { onMeituan: isOnMeituan, onEleme: isOnEleme } = getPlatformStatus(
        store, meituanConfidence, elemeConfidence,
      );

      // 平台视觉：双平台红色、美团黄色、饿了么蓝色、普通门店绿色。
      let markerColor;
      if (isOnMeituan && isOnEleme) {
        markerColor = '#ef4444';
      } else if (isOnMeituan) {
        markerColor = '#facc15';
      } else if (isOnEleme) {
        markerColor = '#3b82f6';
      } else {
        markerColor = '#52c41a';
      }

      const selected = selectedStoreKeys?.includes(store._key);
      const icon = nationalOverview
        ? createOverviewIcon(AMap, markerColor, selected)
        : createMarkerIcon(AMap, markerColor, selected);
      const marker = new AMap.Marker({
        position: [parseFloat(store.lng), parseFloat(store.lat)],
        icon,
        offset: nationalOverview ? new AMap.Pixel(-10, -27) : new AMap.Pixel(-18, -48),
        zIndex: 120,
      });

      // 鼠标悬浮显示信息 - 使用自定义tooltip
      marker.on('mouseover', (e) => {
        // 获取鼠标在屏幕上的位置
        const pixel = mapInstance.current.lngLatToContainer(marker.getPosition());
        setTooltipPosition({ x: pixel.x, y: pixel.y });
        setHoveredStore(store);
      });

      marker.on('mouseout', () => {
        setHoveredStore(null);
      });
      if (!nationalOverview) {
        marker.on('click', () => onStoreToggle?.(store));
      }
      marker.__storeKey = store._key;
      marker.__markerColor = markerColor;
      marker.__overview = nationalOverview;

      marker.setMap(mapInstance.current);
      return marker;
    }).filter(m => m !== null);

    markersRef.current = markers;

    // 只在数据首次加载或变化时自动调整视野（且 shouldAutoFit 为 true）
    const storesChanged = prevStoresRef.current !== stores;
    if (storesChanged && markers.length > 0 && shouldAutoFit && (!boundaries || boundaries.length === 0)) {
      // Region changes are fitted by the boundary effect. Markers are only the
      // fallback when no administrative boundary is available.
      userViewportChangeRef.current = false;
      ignoreViewportUntilRef.current = Date.now() + 1800;
      mapInstance.current.setFitView(markers);
      onAutoFitComplete?.();
    }

    prevStoresRef.current = stores;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, boundaries, shouldAutoFit, onStoreToggle, meituanConfidence, elemeConfidence, onAutoFitComplete, nationalOverview, currentZoom]);

  // Selection only changes marker appearance. Keeping marker instances prevents
  // selection clicks from causing overlay rebuilds and stray viewport events.
  useEffect(() => {
    if (!window.AMap) return;
    const selected = new Set(selectedStoreKeys || []);
    markersRef.current.forEach(marker => {
      const createIcon = marker.__overview ? createOverviewIcon : createMarkerIcon;
      marker.setIcon(createIcon(window.AMap, marker.__markerColor, selected.has(marker.__storeKey)));
    });
  }, [selectedStoreKeys]);

  // 绘制行政区边界
  useEffect(() => {
    if (!mapInstance.current || !window.AMap) return;

    const AMap = window.AMap;

    // 清除旧的边界
    polygonsRef.current.forEach(polygon => polygon.setMap(null));
    polygonsRef.current = [];
    boundaryLabelsRef.current.forEach(label => label.setMap(null));
    boundaryLabelsRef.current = [];

    if (!boundaries || boundaries.length === 0) {
      prevBoundariesRef.current = boundaries;
      return;
    }

    // 绘制每个行政区的边界
    const polygons = [];
    const boundaryLabels = [];

    const getBoundaryLevel = boundary => {
      if (boundary.street) return 'street';
      if (boundary.district) return 'district';
      if (boundary.city) return 'city';
      return 'province';
    };

    const levelStyles = {
      province: { stroke: '#334155', weight: 2.6, opacity: 0.9, maxLabelZoom: 8 },
      city: { stroke: '#475569', weight: 2.3, opacity: 0.88, maxLabelZoom: 10 },
      district: { stroke: '#64748b', weight: 2, opacity: 0.86, maxLabelZoom: 12 },
      street: { stroke: '#64748b', weight: 1.7, opacity: 0.82, maxLabelZoom: 16 },
    };

    const getBoundaryName = boundary => (
      boundary.area_name || boundary.street || boundary.district || boundary.city || boundary.province || ''
    );

    const getPathCenter = paths => {
      const points = paths.flatMap(path => Array.isArray(path) ? path : []);
      if (!points.length) return null;
      let minLng = Infinity;
      let maxLng = -Infinity;
      let minLat = Infinity;
      let maxLat = -Infinity;
      points.forEach(point => {
        const lng = Number(Array.isArray(point) ? point[0] : point?.lng);
        const lat = Number(Array.isArray(point) ? point[1] : point?.lat);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
      return Number.isFinite(minLng) ? [(minLng + maxLng) / 2, (minLat + maxLat) / 2] : null;
    };
    boundaries.forEach((boundary, index) => {
      if (!boundary.polygon || boundary.polygon.length === 0) {
        return;
      }

      // 根据是否选中，使用不同的样式
      const isSelected = boundary.isSelected === true;
      const boundaryLevel = getBoundaryLevel(boundary);
      const boundaryStyle = levelStyles[boundaryLevel];

      // boundary.polygon 现在是一个数组：
      // - 单个POLYGON: [[[lng, lat], [lng, lat], ...]]
      // - MULTIPOLYGON: [[[lng, lat], ...], [[lng, lat], ...], ...]
      const polygonPaths = boundary.polygon;

      // 为每个子多边形创建一个Polygon对象
      polygonPaths.forEach((path, subIndex) => {
        if (!path || path.length === 0) {
          return;
        }

        const polygon = new AMap.Polygon({
          path: path,
          strokeColor: isSelected ? '#2563eb' : boundaryStyle.stroke,
          strokeWeight: isSelected ? boundaryStyle.weight + 0.8 : boundaryStyle.weight,
          strokeOpacity: isSelected ? 1 : boundaryStyle.opacity,
          fillColor: isSelected ? '#93c5fd' : '#dbeafe',
          fillOpacity: isSelected ? 0.1 : 0.02,
          zIndex: isSelected ? 10 : 5,  // 选中的在上层
          extData: boundary,  // 存储边界信息，用于悬停显示
        });

        // 添加鼠标悬停事件
        polygon.on('mouseover', (e) => {
          const pixel = mapInstance.current.lngLatToContainer(e.lnglat);
          setTooltipPosition({ x: pixel.x, y: pixel.y });
          // 只有当边界有有效的区域名称时才设置hoveredBoundary
          const hasValidName = boundary.area_name || boundary.street || boundary.district || boundary.city || boundary.province;
          if (hasValidName) {
            setHoveredBoundary(boundary);
          } else {
            setHoveredBoundary(null);
          }
        });

        polygon.on('mouseout', () => {
          setHoveredBoundary(null);
        });

        // 添加双击事件用于下钻
        polygon.on('dblclick', (e) => {
          // 阻止事件冒泡和默认行为
          if (e.originEvent) {
            e.originEvent.stopPropagation();
            e.originEvent.preventDefault();
          }
          // 模拟鼠标释放事件，防止地图拖拽状态
          const mouseUpEvent = new MouseEvent('mouseup', {
            bubbles: true,
            cancelable: true,
            view: window
          });
          document.dispatchEvent(mouseUpEvent);
          mapContainer.current?.dispatchEvent(mouseUpEvent);

          if (onDrillDown && boundary) {
            onDrillDown(boundary);
          }
        });

        polygon.setMap(mapInstance.current);
        polygons.push(polygon);
      });

      const boundaryName = getBoundaryName(boundary);
      const labelCenter = getPathCenter(polygonPaths);
      if (boundaryName && labelCenter) {
        const label = new AMap.Text({
          text: boundaryName,
          position: labelCenter,
          anchor: 'center',
          zIndex: 90,
          clickable: false,
          bubble: true,
          style: {
            padding: boundaryLevel === 'street' ? '2px 5px' : '3px 7px',
            background: 'rgba(255,255,255,0.88)',
            border: '1px solid rgba(100,116,139,0.48)',
            borderRadius: '3px',
            boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
            color: '#334155',
            fontSize: boundaryLevel === 'street' ? '11px' : '12px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
          },
        });
        label.__maxZoom = boundaryStyle.maxLabelZoom;
        label.setMap(mapInstance.current.getZoom() <= boundaryStyle.maxLabelZoom ? mapInstance.current : null);
        boundaryLabels.push(label);
      }
    });

    polygonsRef.current = polygons;
    boundaryLabelsRef.current = boundaryLabels;

    // 只在边界数据首次加载或变化时自动调整视野（且 shouldAutoFit 为 true）
    const boundariesChanged = prevBoundariesRef.current !== boundaries;
    if (boundariesChanged && polygons.length > 0 && shouldAutoFit) {
      // 如果有边界，优先调整视野到边界
      userViewportChangeRef.current = false;
      ignoreViewportUntilRef.current = Date.now() + 1800;
      mapInstance.current.setFitView(polygons);
      onAutoFitComplete?.();
    }

    prevBoundariesRef.current = boundaries;
  }, [boundaries, shouldAutoFit, onDrillDown, onAutoFitComplete]);

  // Zooming only changes administrative label visibility. Reusing the existing
  // polygons prevents an entire city-boundary layer teardown on every wheel step.
  useEffect(() => {
    if (!mapInstance.current) return;
    boundaryLabelsRef.current.forEach(label => {
      label.setMap(currentZoom <= label.__maxZoom ? mapInstance.current : null);
    });
  }, [currentZoom]);

  // 颜色映射函数：根据归一化需求指数 (0-100) 返回颜色
  // 使用蓝色深浅表示，需求强度越大颜色越深，每档10分
  const getColorByNormalizedDemand = (normalizedDemand) => {
    if (normalizedDemand >= 90) {
      return 'rgba(0, 51, 153, 0.7)';      // 90-100: 最深蓝
    } else if (normalizedDemand >= 80) {
      return 'rgba(26, 77, 179, 0.7)';     // 80-90
    } else if (normalizedDemand >= 70) {
      return 'rgba(51, 102, 204, 0.7)';    // 70-80
    } else if (normalizedDemand >= 60) {
      return 'rgba(77, 128, 230, 0.7)';    // 60-70
    } else if (normalizedDemand >= 50) {
      return 'rgba(102, 153, 255, 0.7)';   // 50-60
    } else if (normalizedDemand >= 40) {
      return 'rgba(128, 170, 255, 0.7)';   // 40-50
    } else if (normalizedDemand >= 30) {
      return 'rgba(153, 187, 255, 0.7)';   // 30-40
    } else if (normalizedDemand >= 20) {
      return 'rgba(179, 204, 255, 0.7)';   // 20-30
    } else if (normalizedDemand >= 10) {
      return 'rgba(204, 221, 255, 0.7)';   // 10-20
    } else {
      return 'rgba(230, 238, 255, 0.7)';   // 0-10: 最浅蓝
    }
  };

  // 综合需求采用独立的紫色色阶，避免与单项需求的蓝色色阶混淆。
  const getColorByNormalizedComposite = (normalizedDemand) => {
    if (normalizedDemand >= 90) return 'rgba(76, 29, 149, 0.7)';
    if (normalizedDemand >= 80) return 'rgba(91, 33, 182, 0.7)';
    if (normalizedDemand >= 70) return 'rgba(109, 40, 217, 0.7)';
    if (normalizedDemand >= 60) return 'rgba(124, 58, 237, 0.7)';
    if (normalizedDemand >= 50) return 'rgba(139, 92, 246, 0.7)';
    if (normalizedDemand >= 40) return 'rgba(167, 139, 250, 0.7)';
    if (normalizedDemand >= 30) return 'rgba(196, 181, 253, 0.7)';
    if (normalizedDemand >= 20) return 'rgba(221, 214, 254, 0.7)';
    if (normalizedDemand >= 10) return 'rgba(237, 233, 254, 0.7)';
    return 'rgba(245, 243, 255, 0.7)';
  };

  // 人口图层颜色映射函数：黄色系
  const getColorByNormalizedPopulation = (normalizedPopulation) => {
    if (normalizedPopulation >= 90) {
      return 'rgba(153, 102, 0, 0.7)';      // 90-100: 最深黄/棕
    } else if (normalizedPopulation >= 80) {
      return 'rgba(179, 119, 0, 0.7)';     // 80-90
    } else if (normalizedPopulation >= 70) {
      return 'rgba(204, 136, 0, 0.7)';     // 70-80
    } else if (normalizedPopulation >= 60) {
      return 'rgba(230, 153, 0, 0.7)';     // 60-70
    } else if (normalizedPopulation >= 50) {
      return 'rgba(255, 170, 0, 0.7)';     // 50-60
    } else if (normalizedPopulation >= 40) {
      return 'rgba(255, 187, 51, 0.7)';    // 40-50
    } else if (normalizedPopulation >= 30) {
      return 'rgba(255, 204, 102, 0.7)';   // 30-40
    } else if (normalizedPopulation >= 20) {
      return 'rgba(255, 221, 153, 0.7)';   // 20-30
    } else if (normalizedPopulation >= 10) {
      return 'rgba(255, 238, 204, 0.7)';   // 10-20
    } else {
      return 'rgba(255, 247, 230, 0.7)';   // 0-10: 最浅黄
    }
  };

  // 人口指数文字颜色
  const getPopulationIndexColor = (normalizedPopulation) => {
    if (normalizedPopulation >= 50) {
      return '#996600';  // 深黄/棕色
    } else {
      return '#cc8800';  // 中等黄色
    }
  };

  // 收入图层颜色映射函数：红色系
  const getColorByNormalizedIncome = (normalizedIncome) => {
    if (normalizedIncome >= 90) {
      return 'rgba(153, 0, 0, 0.7)';        // 90-100: 最深红
    } else if (normalizedIncome >= 80) {
      return 'rgba(179, 26, 26, 0.7)';     // 80-90
    } else if (normalizedIncome >= 70) {
      return 'rgba(204, 51, 51, 0.7)';     // 70-80
    } else if (normalizedIncome >= 60) {
      return 'rgba(230, 77, 77, 0.7)';     // 60-70
    } else if (normalizedIncome >= 50) {
      return 'rgba(255, 102, 102, 0.7)';   // 50-60
    } else if (normalizedIncome >= 40) {
      return 'rgba(255, 128, 128, 0.7)';   // 40-50
    } else if (normalizedIncome >= 30) {
      return 'rgba(255, 153, 153, 0.7)';   // 30-40
    } else if (normalizedIncome >= 20) {
      return 'rgba(255, 179, 179, 0.7)';   // 20-30
    } else if (normalizedIncome >= 10) {
      return 'rgba(255, 204, 204, 0.7)';   // 10-20
    } else {
      return 'rgba(255, 230, 230, 0.7)';   // 0-10: 最浅红
    }
  };

  // 收入指数文字颜色
  const getIncomeIndexColor = (normalizedIncome) => {
    if (normalizedIncome >= 50) {
      return '#990000';  // 深红色
    } else {
      return '#cc3333';  // 中等红色
    }
  };

  // 生成边界匹配的复合键
  const getBoundaryKey = (province, city, district, street) => {
    const municipalities = ['北京市', '上海市', '天津市', '重庆市'];
    const normalizedProvince = String(province || '').trim();
    let normalizedCity = String(city || '').trim();

    if (municipalities.includes(normalizedProvince) && normalizedCity) {
      normalizedCity = normalizedProvince;
    }

    return [normalizedProvince, normalizedCity, district, street]
      .map(value => String(value || '').trim())
      .join('|');
  };

  // 渲染需求图层（性能优化版本）
  useEffect(() => {
    if (!mapInstance.current || !window.AMap) return;

    const AMap = window.AMap;

    // 清除旧的需求图层多边形
    demandPolygonsRef.current.forEach(polygon => polygon.setMap(null));
    demandPolygonsRef.current = [];

    // 如果未开启需求图层或没有需求数据，直接返回
    if (!demandLayerEnabled || !demandData || demandData.length === 0) {
      return;
    }

    // 【优化1】构建边界数据的 Map 索引，将 O(n*m) 降为 O(n)
    const boundaryMap = new Map();

    boundaries.forEach(boundary => {
      // 判断各级别是否有值（空字符串视为无值）
      const hasStreet = boundary.street && boundary.street.trim() !== '';
      const hasDistrict = boundary.district && boundary.district.trim() !== '';
      const hasCity = boundary.city && boundary.city.trim() !== '';
      const hasProvince = boundary.province && boundary.province.trim() !== '';

      // 生成匹配键
      let key;
      if (hasStreet) {
        // 街道级
        key = getBoundaryKey(boundary.province, boundary.city, boundary.district, boundary.street);
      } else if (hasDistrict) {
        // 区县级
        key = getBoundaryKey(boundary.province, boundary.city, boundary.district, '');
      } else if (hasCity) {
        // 城市级
        key = getBoundaryKey(boundary.province, boundary.city, '', '');
      } else if (hasProvince) {
        // 省份级
        key = getBoundaryKey(boundary.province, '', '', '');
      }

      if (key) {
        boundaryMap.set(key, boundary);
      }
    });

    // 创建多边形的辅助函数
    const createPolygon = (path, fillColor, demand) => {
      const polygon = new AMap.Polygon({
        path: path,
        strokeColor: demandLayerMode === 'composite' ? '#8b5cf6' : '#60a5fa',
        strokeWeight: 1.6,
        strokeOpacity: 0.9,
        fillColor: fillColor,
        fillOpacity: 0.42,
        zIndex: 50,
      });

      // 添加鼠标事件
      polygon.on('mouseover', (e) => {
        polygon.setOptions({
          fillOpacity: 0.56,
          strokeWeight: 2.2,
        });
        setHoveredBoundary({
          ...demand,
          name: demand.area_name,
        });
        const pixel = mapInstance.current.lngLatToContainer(e.lnglat);
        setTooltipPosition({ x: pixel.x, y: pixel.y });
      });

      polygon.on('mouseout', () => {
        polygon.setOptions({
          fillOpacity: 0.42,
          strokeWeight: 1.6,
        });
        setHoveredBoundary(null);
      });

      // 添加双击事件用于下钻
      polygon.on('dblclick', (e) => {
        if (e.originEvent) {
          e.originEvent.stopPropagation();
          e.originEvent.preventDefault();
        }
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.dispatchEvent(mouseUpEvent);
        mapContainer.current?.dispatchEvent(mouseUpEvent);

        if (onDrillDown && demand) {
          onDrillDown(demand);
        }
      });

      // 直接添加到地图
      polygon.setMap(mapInstance.current);

      return polygon;
    };

    // 收集所有多边形
    const demandPolygons = [];

    demandData.forEach((demand, index) => {
      // Missing regional data is not zero demand and must not be painted as the lightest band.
      if (demand.normalized_demand === null || demand.normalized_demand === undefined) return;
      // 判断各级别是否有值（空字符串视为无值）
      const hasStreet = demand.street && demand.street.trim() !== '';
      const hasDistrict = demand.district && demand.district.trim() !== '';
      const hasCity = demand.city && demand.city.trim() !== '';

      // 【优化1】使用 Map 快速查找边界，O(1) 复杂度
      let matchKey;
      if (hasStreet) {
        matchKey = getBoundaryKey(demand.province, demand.city, demand.district, demand.street);
      } else if (hasDistrict) {
        matchKey = getBoundaryKey(demand.province, demand.city, demand.district, '');
      } else if (hasCity) {
        matchKey = getBoundaryKey(demand.province, demand.city, '', '');
      } else {
        matchKey = getBoundaryKey(demand.province, '', '', '');
      }

      const matchingBoundary = boundaryMap.get(matchKey);

      if (!matchingBoundary || !matchingBoundary.polygon) {
        return;
      }

      const fillColor = demandLayerMode === 'composite'
        ? getColorByNormalizedComposite(demand.normalized_demand)
        : getColorByNormalizedDemand(demand.normalized_demand);
      const polygonData = matchingBoundary.polygon;

      if (Array.isArray(polygonData) && polygonData.length > 0) {
        if (Array.isArray(polygonData[0][0])) {
          // MULTIPOLYGON
          polygonData.forEach(subPolygonPath => {
            demandPolygons.push(createPolygon(subPolygonPath, fillColor, demand));
          });
        } else {
          // POLYGON
          demandPolygons.push(createPolygon(polygonData[0], fillColor, demand));
        }
      }
    });

    // 保存引用以便后续清除
    demandPolygonsRef.current = demandPolygons;

  }, [demandLayerEnabled, demandData, boundaries, demandLayerMode, onDrillDown]);

  // 渲染人口图层（黄色系）
  useEffect(() => {
    if (!mapInstance.current || !window.AMap) return;

    const AMap = window.AMap;

    // 清除旧的人口图层多边形
    populationPolygonsRef.current.forEach(polygon => polygon.setMap(null));
    populationPolygonsRef.current = [];

    // 如果未开启人口图层或没有人口数据，直接返回
    if (!populationLayerEnabled || !populationData || populationData.length === 0) {
      return;
    }

    // 构建边界数据的 Map 索引
    const boundaryMap = new Map();
    boundaries.forEach(boundary => {
      const hasStreet = boundary.street && boundary.street.trim() !== '';
      const hasDistrict = boundary.district && boundary.district.trim() !== '';
      const hasCity = boundary.city && boundary.city.trim() !== '';
      const hasProvince = boundary.province && boundary.province.trim() !== '';

      let key;
      if (hasStreet) {
        key = getBoundaryKey(boundary.province, boundary.city, boundary.district, boundary.street);
      } else if (hasDistrict) {
        key = getBoundaryKey(boundary.province, boundary.city, boundary.district, '');
      } else if (hasCity) {
        key = getBoundaryKey(boundary.province, boundary.city, '', '');
      } else if (hasProvince) {
        key = getBoundaryKey(boundary.province, '', '', '');
      }

      if (key) {
        boundaryMap.set(key, boundary);
      }
    });

    // 创建多边形
    const createPolygon = (path, fillColor, data) => {
      const polygon = new AMap.Polygon({
        path: path,
        strokeColor: '#996600',
        strokeWeight: 1.6,
        strokeOpacity: 0.85,
        fillColor: fillColor,
        fillOpacity: 0.42,
        zIndex: 50,
      });

      polygon.on('mouseover', (e) => {
        polygon.setOptions({ fillOpacity: 0.56, strokeWeight: 2.2 });
        setHoveredBoundary({
          ...data,
          name: data.area_name,
          layerType: 'population',
        });
        const pixel = mapInstance.current.lngLatToContainer(e.lnglat);
        setTooltipPosition({ x: pixel.x, y: pixel.y });
      });

      polygon.on('mouseout', () => {
        polygon.setOptions({ fillOpacity: 0.42, strokeWeight: 1.6 });
        setHoveredBoundary(null);
      });

      // 添加双击事件用于下钻
      polygon.on('dblclick', (e) => {
        if (e.originEvent) {
          e.originEvent.stopPropagation();
          e.originEvent.preventDefault();
        }
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.dispatchEvent(mouseUpEvent);
        mapContainer.current?.dispatchEvent(mouseUpEvent);

        if (onDrillDown && data) {
          onDrillDown(data);
        }
      });

      polygon.setMap(mapInstance.current);
      return polygon;
    };

    const populationPolygons = [];

    populationData.forEach((data) => {
      const hasStreet = data.street && data.street.trim() !== '';
      const hasDistrict = data.district && data.district.trim() !== '';
      const hasCity = data.city && data.city.trim() !== '';

      let matchKey;
      if (hasStreet) {
        matchKey = getBoundaryKey(data.province, data.city, data.district, data.street);
      } else if (hasDistrict) {
        matchKey = getBoundaryKey(data.province, data.city, data.district, '');
      } else if (hasCity) {
        matchKey = getBoundaryKey(data.province, data.city, '', '');
      } else {
        matchKey = getBoundaryKey(data.province, '', '', '');
      }

      const matchingBoundary = boundaryMap.get(matchKey);

      if (!matchingBoundary || !matchingBoundary.polygon) {
        return;
      }

      const fillColor = getColorByNormalizedPopulation(data.normalized_population);
      const polygonData = matchingBoundary.polygon;

      if (Array.isArray(polygonData) && polygonData.length > 0) {
        if (Array.isArray(polygonData[0][0])) {
          polygonData.forEach(subPolygonPath => {
            populationPolygons.push(createPolygon(subPolygonPath, fillColor, data));
          });
        } else {
          populationPolygons.push(createPolygon(polygonData[0], fillColor, data));
        }
      }
    });

    populationPolygonsRef.current = populationPolygons;

  }, [populationLayerEnabled, populationData, boundaries, onDrillDown]);

  // 渲染收入图层（红色系）
  useEffect(() => {
    if (!mapInstance.current || !window.AMap) return;

    const AMap = window.AMap;

    // 清除旧的收入图层多边形
    incomePolygonsRef.current.forEach(polygon => polygon.setMap(null));
    incomePolygonsRef.current = [];

    // 如果未开启收入图层或没有收入数据，直接返回
    if (!incomeLayerEnabled || !incomeData || incomeData.length === 0) {
      return;
    }

    // 构建边界数据的 Map 索引
    const boundaryMap = new Map();
    boundaries.forEach(boundary => {
      const hasStreet = boundary.street && boundary.street.trim() !== '';
      const hasDistrict = boundary.district && boundary.district.trim() !== '';
      const hasCity = boundary.city && boundary.city.trim() !== '';
      const hasProvince = boundary.province && boundary.province.trim() !== '';

      let key;
      if (hasStreet) {
        key = getBoundaryKey(boundary.province, boundary.city, boundary.district, boundary.street);
      } else if (hasDistrict) {
        key = getBoundaryKey(boundary.province, boundary.city, boundary.district, '');
      } else if (hasCity) {
        key = getBoundaryKey(boundary.province, boundary.city, '', '');
      } else if (hasProvince) {
        key = getBoundaryKey(boundary.province, '', '', '');
      }

      if (key) {
        boundaryMap.set(key, boundary);
      }
    });

    // 创建多边形
    const createPolygon = (path, fillColor, data) => {
      const polygon = new AMap.Polygon({
        path: path,
        strokeColor: '#990000',
        strokeWeight: 1.6,
        strokeOpacity: 0.85,
        fillColor: fillColor,
        fillOpacity: 0.42,
        zIndex: 50,
      });

      polygon.on('mouseover', (e) => {
        polygon.setOptions({ fillOpacity: 0.56, strokeWeight: 2.2 });
        setHoveredBoundary({
          ...data,
          name: data.area_name,
          layerType: 'income',
        });
        const pixel = mapInstance.current.lngLatToContainer(e.lnglat);
        setTooltipPosition({ x: pixel.x, y: pixel.y });
      });

      polygon.on('mouseout', () => {
        polygon.setOptions({ fillOpacity: 0.42, strokeWeight: 1.6 });
        setHoveredBoundary(null);
      });

      // 添加双击事件用于下钻
      polygon.on('dblclick', (e) => {
        if (e.originEvent) {
          e.originEvent.stopPropagation();
          e.originEvent.preventDefault();
        }
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.dispatchEvent(mouseUpEvent);
        mapContainer.current?.dispatchEvent(mouseUpEvent);

        if (onDrillDown && data) {
          onDrillDown(data);
        }
      });

      polygon.setMap(mapInstance.current);
      return polygon;
    };

    const incomePolygons = [];

    incomeData.forEach((data) => {
      const hasStreet = data.street && data.street.trim() !== '';
      const hasDistrict = data.district && data.district.trim() !== '';
      const hasCity = data.city && data.city.trim() !== '';

      let matchKey;
      if (hasStreet) {
        matchKey = getBoundaryKey(data.province, data.city, data.district, data.street);
      } else if (hasDistrict) {
        matchKey = getBoundaryKey(data.province, data.city, data.district, '');
      } else if (hasCity) {
        matchKey = getBoundaryKey(data.province, data.city, '', '');
      } else {
        matchKey = getBoundaryKey(data.province, '', '', '');
      }

      const matchingBoundary = boundaryMap.get(matchKey);

      if (!matchingBoundary || !matchingBoundary.polygon) {
        return;
      }

      const fillColor = getColorByNormalizedIncome(data.normalized_income);
      const polygonData = matchingBoundary.polygon;

      if (Array.isArray(polygonData) && polygonData.length > 0) {
        if (Array.isArray(polygonData[0][0])) {
          polygonData.forEach(subPolygonPath => {
            incomePolygons.push(createPolygon(subPolygonPath, fillColor, data));
          });
        } else {
          incomePolygons.push(createPolygon(polygonData[0], fillColor, data));
        }
      }
    });

    incomePolygonsRef.current = incomePolygons;

  }, [incomeLayerEnabled, incomeData, boundaries, onDrillDown]);

  return (
    <div style={styles.container}>
      <div ref={mapContainer} className="compass-map-canvas" style={styles.map}></div>

      {/* 加载动画覆盖层 */}
      {loading && (
        <div style={styles.loadingOverlay}>
          <Spin size="large" tip="数据加载中..." />
        </div>
      )}

      {/* 左上角层级导航 */}
      <div style={styles.backButtonContainer}>
          {currentRegion && currentRegion.province && (
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={onGoBack}
            style={styles.backButton}
          >
            返回上一级
          </Button>
          )}
          <div style={styles.currentLocation}>
            <strong>当前级别：</strong>
            {currentRegion?.province || '全国'}
            {currentRegion?.city && ` > ${currentRegion.city}`}
            {currentRegion?.district && ` > ${currentRegion.district}`}
            {currentRegion?.street && ` > ${currentRegion.street}`}
          </div>
      </div>

      {/* 左下角水印 */}
      <div style={styles.watermark}>供需罗盘 · 门店版</div>

      {/* 统计面板 */}
      <div style={styles.statsPanel}>
        <div style={styles.statsText}>
          {/* 筛选区域的总门店数（不随地图缩放变化） */}
          {nationalOverview ? '请选择省份查看门店' : <>共 <strong>{totalCount || 0}</strong> 家门店</>}
          {stores && stores.length > 0 && (
            <div style={{ fontSize: '12px', marginTop: '5px', color: '#666' }}>
              {/* 当前可视区域显示的点位数（随地图缩放动态变化） */}
              可视区域: <strong style={{ color: '#1890ff' }}>
                {stores.length}
              </strong> 个点位
            </div>
          )}
          <div style={{ fontSize: '12px', marginTop: '5px', color: '#999' }}>
            缩放级别: <strong>{currentZoom.toFixed(1)}</strong>
            {nationalOverview ? ' (全国分层概览)' : ' (最多100个点位)'}
          </div>
        </div>
      </div>

      {/* 右下角图例和缩放控件容器 */}
      <div style={styles.bottomRightContainer}>
        {/* 图例区域 */}
        <div style={styles.legendsWrapper}>
          {/* 门店点位图例 */}
          <div style={styles.markerLegend}>
            <div style={styles.markerLegendRow}>
              <MarkerLegendIcon color="#ef4444" />
              <span>双平台</span>
            </div>
            <div style={styles.markerLegendRow}>
              <MarkerLegendIcon color="#facc15" />
              <span>美团</span>
            </div>
            <div style={styles.markerLegendRow}>
              <MarkerLegendIcon color="#3b82f6" />
              <span>饿了么</span>
            </div>
            <div style={styles.markerLegendRow}>
              <MarkerLegendIcon color="#52c41a" />
              <span>线下</span>
            </div>
            <div style={styles.markerLegendRow}>
              <MarkerLegendIcon color="#fff" selected />
              <span>已圈选</span>
            </div>
          </div>

          {/* 需求图层色阶图例 - 竖向渐变色条 */}
          {demandLayerEnabled && demandData && demandData.length > 0 && (
            <div style={styles.legendItem2}>
              <div style={styles.legendTitle}>{demandLayerMode === 'composite' ? '综合需求分' : '需求强度'}</div>
              <div style={styles.gradientLegend}>
                <span style={styles.gradientLabelTop}>100</span>
                <div style={{
                  ...styles.gradientBar,
                  ...(demandLayerMode === 'composite' ? styles.compositeGradientBar : {}),
                }}></div>
                <span style={styles.gradientLabelBottom}>0</span>
              </div>
            </div>
          )}

          {/* 人口图层色阶图例 - 黄色系 */}
          {populationLayerEnabled && populationData && populationData.length > 0 && (
            <div style={styles.legendItem2}>
              <div style={styles.legendTitle}>人口规模</div>
              <div style={styles.gradientLegend}>
                <span style={styles.gradientLabelTop}>100</span>
                <div style={{
                  ...styles.gradientBar,
                  background: 'linear-gradient(to top, rgba(255, 247, 230, 0.9), rgba(255, 221, 153, 0.9), rgba(255, 170, 0, 0.9), rgba(204, 136, 0, 0.9), rgba(153, 102, 0, 0.9))',
                }}></div>
                <span style={styles.gradientLabelBottom}>0</span>
              </div>
            </div>
          )}

          {/* 收入图层色阶图例 - 红色系 */}
          {incomeLayerEnabled && incomeData && incomeData.length > 0 && (
            <div style={styles.legendItem2}>
              <div style={styles.legendTitle}>
                {selectedIncomeLevel === 'high' ? '高收入人口' :
                 selectedIncomeLevel === 'middle' ? '中收入人口' : '低收入人口'}
              </div>
              <div style={styles.gradientLegend}>
                <span style={styles.gradientLabelTop}>100</span>
                <div style={{
                  ...styles.gradientBar,
                  background: 'linear-gradient(to top, rgba(255, 230, 230, 0.9), rgba(255, 179, 179, 0.9), rgba(255, 102, 102, 0.9), rgba(204, 51, 51, 0.9), rgba(153, 0, 0, 0.9))',
                }}></div>
                <span style={styles.gradientLabelBottom}>0</span>
              </div>
            </div>
          )}
        </div>

        {/* 缩放控件 */}
        <div style={styles.zoomControl}>
          <Button
            type="text"
            icon={<PlusOutlined />}
             onClick={() => {
               if (mapInstance.current) {
                 userViewportChangeRef.current = true;
                 lastUserViewportChangeAtRef.current = Date.now();
                 const newZoom = Math.min(currentZoom + 1, 20);
                setCurrentZoom(newZoom);
                mapInstance.current.setZoom(newZoom);
              }
            }}
            style={styles.zoomButton}
          />
          <div style={styles.zoomSliderContainer}>
            <Slider
              vertical
              min={3}
              max={20}
              step={0.5}
              value={currentZoom}
              onChange={(value) => {
               setCurrentZoom(value);
               if (mapInstance.current) {
                 userViewportChangeRef.current = true;
                 lastUserViewportChangeAtRef.current = Date.now();
                 mapInstance.current.setZoom(value);
                }
              }}
              tooltip={{ formatter: (value) => `缩放: ${value}` }}
              style={styles.zoomSlider}
            />
          </div>
          <Button
            type="text"
            icon={<MinusOutlined />}
             onClick={() => {
               if (mapInstance.current) {
                 userViewportChangeRef.current = true;
                 lastUserViewportChangeAtRef.current = Date.now();
                 const newZoom = Math.max(currentZoom - 1, 3);
                setCurrentZoom(newZoom);
                mapInstance.current.setZoom(newZoom);
              }
            }}
            style={styles.zoomButton}
          />
        </div>
      </div>

      {/* 自定义tooltip - 鼠标悬浮显示门店信息 */}
      {hoveredStore && (
        <div
          style={{
            ...styles.tooltip,
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y - 10}px`,
          }}
        >
          <h3 style={styles.tooltipTitle}>
            {hoveredStore.name || '未知门店'}
          </h3>

          {hoveredStore.photos && hoveredStore.photos.length > 0 && (
            <img
              src={hoveredStore.photos[0]}
              alt="门店图片"
              style={styles.tooltipImage}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}

          <div style={styles.tooltipInfo}>
            <p style={styles.tooltipItem}>
              <strong>地址：</strong>{hoveredStore.address || '未知'}
            </p>
            <p style={styles.tooltipItem}>
              <strong>电话：</strong>{hoveredStore.tel || '未知'}
            </p>
            <p style={styles.tooltipItem}>
              <strong>类型：</strong>
              {hoveredStore.parentType || ''}
              {hoveredStore.childType && ` - ${hoveredStore.childType}`}
            </p>
            {/* 双平台门店标记 */}
            {hoveredStore.is_on_meituan === 1 &&
             hoveredStore.mapping_meituan_similarity >= meituanConfidence &&
             hoveredStore.is_on_eleme === 1 &&
             hoveredStore.mapping_eleme_similarity >= elemeConfidence && (
              <p style={{...styles.tooltipItem, color: '#FF0000', fontWeight: 'bold'}}>
                🔥 美团+饿了么 双平台门店
              </p>
            )}
            {/* 美团信息 */}
            {hoveredStore.is_on_meituan === 1 &&
             hoveredStore.mapping_meituan_similarity >= meituanConfidence && (
              <>
                <p style={{...styles.tooltipItem, color: '#FFD100', fontWeight: 'bold'}}>
                  🛵 美团线上门店
                </p>
                {hoveredStore.mapping_meituan_name && (
                  <p style={styles.tooltipItem}>
                    <strong>美团名称：</strong>{hoveredStore.mapping_meituan_name}
                  </p>
                )}
                {hoveredStore.mapping_meituan_similarity && (
                  <p style={styles.tooltipItem}>
                    <strong>美团匹配度：</strong>{(hoveredStore.mapping_meituan_similarity * 100).toFixed(1)}%
                  </p>
                )}
              </>
            )}
            {/* 饿了么信息 */}
            {hoveredStore.is_on_eleme === 1 &&
             hoveredStore.mapping_eleme_similarity >= elemeConfidence && (
              <>
                <p style={{...styles.tooltipItem, color: '#1890FF', fontWeight: 'bold'}}>
                  🍔 饿了么线上门店
                </p>
                {hoveredStore.mapping_eleme_name && (
                  <p style={styles.tooltipItem}>
                    <strong>饿了么名称：</strong>{hoveredStore.mapping_eleme_name}
                  </p>
                )}
                {hoveredStore.mapping_eleme_similarity && (
                  <p style={styles.tooltipItem}>
                    <strong>饿了么匹配度：</strong>{(hoveredStore.mapping_eleme_similarity * 100).toFixed(1)}%
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* 自定义tooltip - 鼠标悬浮显示区域信息 */}
      {/* 只有当有有效内容时才显示tooltip */}
      {hoveredBoundary && (() => {
        // 计算要显示的区域名称
        const areaDisplayName = hoveredBoundary.area_name || hoveredBoundary.street || hoveredBoundary.district || hoveredBoundary.city || hoveredBoundary.province;
        // 检查是否有数据图层信息
        const hasDemandData = demandLayerEnabled && hoveredBoundary.normalized_demand !== undefined;
        const hasPopulationData = populationLayerEnabled && hoveredBoundary.normalized_population !== undefined;
        const hasIncomeData = incomeLayerEnabled && hoveredBoundary.normalized_income !== undefined;
        // 只有当有区域名称或有数据图层信息时才显示
        if (!areaDisplayName && !hasDemandData && !hasPopulationData && !hasIncomeData) {
          return null;
        }
        return (
        <div
          style={{
            ...styles.boundaryTooltip,
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y - 10}px`,
            ...(demandLayerEnabled && hoveredBoundary.normalized_demand !== undefined
              ? styles.demandTooltip
              : {}),
            ...(populationLayerEnabled && hoveredBoundary.normalized_population !== undefined
              ? styles.populationTooltip
              : {}),
            ...(incomeLayerEnabled && hoveredBoundary.normalized_income !== undefined
              ? styles.incomeTooltip
              : {}),
          }}
        >
          <div style={styles.boundaryTooltipContent}>
            {/* 行政区域名称 - 显示第一个非空值 */}
            {(hoveredBoundary.area_name || hoveredBoundary.street || hoveredBoundary.district || hoveredBoundary.city || hoveredBoundary.province) && (
              <strong style={styles.areaName}>
                {hoveredBoundary.area_name || hoveredBoundary.street || hoveredBoundary.district || hoveredBoundary.city || hoveredBoundary.province}
              </strong>
            )}
            {hoveredBoundary.street && !hoveredBoundary.area_name && (
              <div style={{ fontSize: '13px', marginTop: '4px', color: '#666' }}>
                {hoveredBoundary.street}
              </div>
            )}

            {/* 需求图层信息 - 仅当开启需求图层且有需求数据时显示 */}
            {demandLayerEnabled && hoveredBoundary.normalized_demand !== undefined && (
              <div style={styles.demandInfo}>
                {/* 单项需求图层展示品类，综合图层展示实际参与计算的权重。 */}
                {demandLayerMode !== 'composite' && selectedCategory && selectedCategory.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={styles.demandLabel}>品类:</div>
                    <div style={{ marginTop: '4px' }}>
                      {selectedCategory.map((cat, index) => (
                        <div key={index} style={{
                          color: '#333',
                          fontSize: '13px',
                          lineHeight: '1.6'
                        }}>
                          {cat.length > 1 ? cat[1] : cat[0]}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {demandLayerMode === 'composite' && hoveredBoundary.effectiveWeights && (
                  <div style={{ marginBottom: '8px', color: '#555', fontSize: '12px' }}>
                    实际权重：{[
                      ['category', '品类'], ['age', '年龄'], ['income', '收入'],
                    ].filter(([key]) => hoveredBoundary.effectiveWeights[key] > 0)
                      .map(([key, label]) => `${label}${Math.round(hoveredBoundary.effectiveWeights[key] * 100)}%`).join(' / ') || '暂无可用数据'}
                  </div>
                )}

                {/* 需求指数 */}
                <div style={styles.demandItem}>
                  <span style={styles.demandLabel}>{demandLayerMode === 'composite' ? '街道综合需求分:' : '需求指数:'}</span>
                  <span style={{
                    ...styles.demandValue,
                    color: '#003399',
                    fontWeight: 'bold'
                  }}>
                    {hoveredBoundary.normalized_demand?.toFixed(1) || 0}
                  </span>
                </div>
              </div>
            )}

            {/* 人口图层信息 */}
            {populationLayerEnabled && hoveredBoundary.normalized_population !== undefined && (
              <div style={styles.demandInfo}>
                <div style={{ marginBottom: '8px' }}>
                  <div style={styles.demandLabel}>人口结构:</div>
                  <div style={{ marginTop: '4px', color: '#333', fontSize: '13px' }}>
                    {!selectedPopulationTypes || selectedPopulationTypes.length === 3 ? '总人口' :
                      selectedPopulationTypes.map(t =>
                        t === 'youth' ? '少年' : t === 'labor' ? '劳动力' : '老年'
                      ).join(' + ')
                    }
                  </div>
                </div>
                <div style={styles.demandItem}>
                  <span style={styles.demandLabel}>人口指数:</span>
                  <span style={{
                    ...styles.demandValue,
                    color: getPopulationIndexColor(hoveredBoundary.normalized_population),
                    fontWeight: 'bold'
                  }}>
                    {hoveredBoundary.normalized_population?.toFixed(1) || 0}
                  </span>
                </div>
              </div>
            )}

            {/* 收入图层信息 */}
            {incomeLayerEnabled && hoveredBoundary.normalized_income !== undefined && (
              <div style={styles.demandInfo}>
                <div style={{ marginBottom: '8px' }}>
                  <div style={styles.demandLabel}>收入层级:</div>
                  <div style={{ marginTop: '4px', color: '#333', fontSize: '13px' }}>
                    {selectedIncomeLevel === 'high' ? '高收入人口' :
                     selectedIncomeLevel === 'middle' ? '中收入人口' : '低收入人口'}
                  </div>
                </div>
                <div style={styles.demandItem}>
                  <span style={styles.demandLabel}>收入指数:</span>
                  <span style={{
                    ...styles.demandValue,
                    color: getIncomeIndexColor(hoveredBoundary.normalized_income),
                    fontWeight: 'bold'
                  }}>
                    {hoveredBoundary.normalized_income?.toFixed(1) || 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  map: {
    width: '100%',
    height: '100%',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1500,
  },
  backButtonContainer: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  backButton: {
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  currentLocation: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '8px 12px',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    fontSize: '13px',
    color: '#666',
    maxWidth: '300px',
  },
  watermark: {
    position: 'absolute',
    bottom: '10px',
    left: '10px',
    fontSize: '12px',
    color: '#666',
    zIndex: 1000,
    pointerEvents: 'none',
  },
  statsPanel: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '15px 20px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1000,
  },
  statsText: {
    fontSize: '16px',
    color: '#333',
  },
  tooltip: {
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    padding: '12px 15px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 2000,
    minWidth: '250px',
    maxWidth: '350px',
    pointerEvents: 'none', // 防止tooltip阻挡鼠标事件
  },
  tooltipTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    borderBottom: '1px solid #e8e8e8',
    paddingBottom: '8px',
  },
  tooltipImage: {
    width: '100%',
    maxHeight: '150px',
    objectFit: 'cover',
    borderRadius: '6px',
    marginBottom: '10px',
  },
  tooltipInfo: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.6',
  },
  tooltipItem: {
    margin: '6px 0',
  },
  boundaryTooltip: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '14px',
    zIndex: 2000,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  boundaryTooltipContent: {
    lineHeight: '1.4',
  },
  // 需求图层tooltip样式（更宽更详细）- 浅色背景
  demandTooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    color: '#333',  // 浅色背景用深色文字
    padding: '12px 16px',
    borderRadius: '8px',
    minWidth: '180px',
    whiteSpace: 'normal',
    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
  },
  // 人口图层tooltip样式 - 浅黄色背景
  populationTooltip: {
    backgroundColor: 'rgba(255, 251, 240, 0.98)',
    color: '#333',  // 浅色背景用深色文字
    padding: '12px 16px',
    borderRadius: '8px',
    minWidth: '180px',
    whiteSpace: 'normal',
    boxShadow: '0 2px 12px rgba(153, 102, 0, 0.2)',
  },
  // 收入图层tooltip样式 - 浅红色背景
  incomeTooltip: {
    backgroundColor: 'rgba(255, 245, 245, 0.98)',
    color: '#333',  // 浅色背景用深色文字
    padding: '12px 16px',
    borderRadius: '8px',
    minWidth: '180px',
    whiteSpace: 'normal',
    boxShadow: '0 2px 12px rgba(153, 0, 0, 0.2)',
  },
  areaName: {
    fontSize: '15px',
    display: 'block',
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
    // color 不设置，继承父级的白色（普通边界）或在数据图层样式中覆盖
  },
  demandInfo: {
    marginTop: '4px',
  },
  demandItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '6px',
    fontSize: '13px',
  },
  demandLabel: {
    color: 'rgba(0, 0, 0, 0.65)',
  },
  demandValue: {
    color: '#333',
    marginLeft: '12px',
  },
  // 右下角容器
  bottomRightContainer: {
    position: 'absolute',
    bottom: '30px',
    right: '15px',
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'flex-end',
    zIndex: 1000,
  },
  legendsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
  },
  markerLegend: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '14px 16px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: 'max-content',
  },
  markerLegendRow: {
    display: 'grid',
    gridTemplateColumns: '18px max-content',
    alignItems: 'center',
    columnGap: '9px',
    minHeight: '24px',
    color: '#333',
    fontSize: '13px',
    lineHeight: '24px',
    whiteSpace: 'nowrap',
  },
  legendItem2: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '10px 12px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    minWidth: '60px',
  },
  zoomControl: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '8px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  zoomButton: {
    width: '32px',
    height: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
  },
  zoomSliderContainer: {
    height: '120px',
    display: 'flex',
    justifyContent: 'center',
  },
  zoomSlider: {
    margin: 0,
  },
  legend: {
    position: 'absolute',
    bottom: '120px',
    right: '15px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '12px 15px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1000,
    minWidth: '60px',
  },
  legendTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '10px',
    borderBottom: '1px solid #e8e8e8',
    paddingBottom: '6px',
  },
  legendItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  legendColor: {
    width: '20px',
    height: '14px',
    borderRadius: '2px',
    border: '1px solid #ccc',
  },
  legendLabel: {
    fontSize: '12px',
    color: '#666',
  },
  // 渐变色条图例样式
  gradientLegend: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  gradientBar: {
    width: '20px',
    height: '120px',
    borderRadius: '3px',
    border: '1px solid #ccc',
    background: 'linear-gradient(to top, rgba(230, 238, 255, 0.9), rgba(179, 204, 255, 0.9), rgba(102, 153, 255, 0.9), rgba(51, 102, 204, 0.9), rgba(0, 51, 153, 0.9))',
  },
  compositeGradientBar: {
    background: 'linear-gradient(to top, rgba(245, 243, 255, 0.9), rgba(221, 214, 254, 0.9), rgba(167, 139, 250, 0.9), rgba(124, 58, 237, 0.9), rgba(76, 29, 149, 0.9))',
  },
  gradientLabelTop: {
    fontSize: '12px',
    color: '#333',
    fontWeight: 'bold',
  },
  gradientLabelBottom: {
    fontSize: '12px',
    color: '#333',
    fontWeight: 'bold',
  },
};

export default MapView;
